import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, FileText, Link as LinkIcon, Video, Image, File, 
  ExternalLink, Trash2, User as UserIcon, Building, MapPin, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';

const ResourcesPage = () => {
  const { user, api, isFeatureEnabled } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [viewingResource, setViewingResource] = useState(null);
  const [newResource, setNewResource] = useState({
    title: '',
    description: '',
    url: '',
    file_type: 'link',
    is_company_resource: false,
    state: user?.state || ''
  });

  useEffect(() => {
    loadResources();
  }, []);

  // Mobile header override — title "Resources" + "+" icon (open the
  // create-resource dialog). The in-page Add button has been removed.
  usePageHeader({
    title: 'Resources',
    rightSlot: (
      <button
        onClick={() => setShowCreateDialog(true)}
        className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
        aria-label="Add resource"
        data-testid="header-add-resource-btn"
      >
        <Plus className="w-5 h-5" />
      </button>
    ),
  }, []);

  const loadResources = async () => {
    setLoading(true);
    try {
      const [myRes, companyRes] = await Promise.all([
        api.get('/resources/my'),
        api.get('/resources/company')
      ]);
      setResources([...myRes.data.map(r => ({...r, isCompany: false})), 
                    ...companyRes.data.map(r => ({...r, isCompany: true}))]);
    } catch (error) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateResource = async () => {
    if (!newResource.title || !newResource.url) {
      toast.error('Title and URL are required');
      return;
    }
    try {
      await api.post('/resources', newResource);
      // silent success
      setShowCreateDialog(false);
      setNewResource({
        title: '',
        description: '',
        url: '',
        file_type: 'link',
        is_company_resource: false,
        state: user?.state || ''
      });
      loadResources();
    } catch (error) {
      toast.error('Failed to add resource');
    }
  };

  const handleDeleteResource = async (resourceId) => {
    try {
      await api.delete(`/resources/${resourceId}`);
      // silent success
      loadResources();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  const handleResourceClick = (resource) => {
    // Open every resource externally — most providers (forms, news sites, even
    // YouTube) block iframe embedding via X-Frame-Options, and an in-app iframe
    // also competes with the iOS status bar. Native browser tab handles both.
    if (resource.url) {
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    }
  };

  const getFileTypeIcon = (type) => {
    switch (type) {
      case 'document':
        return <FileText className="w-4 h-4" />;
      case 'video':
        return <Video className="w-4 h-4" />;
      case 'image':
        return <Image className="w-4 h-4" />;
      case 'link':
        return <LinkIcon className="w-4 h-4" />;
      default:
        return <File className="w-4 h-4" />;
    }
  };

  const myResources = resources.filter(r => !r.isCompany);
  const companyResources = resources.filter(r => r.isCompany);
  const canAddCompanyResource = ['admin', 'supervisor'].includes(user?.role);

  // Full-page native Resource Viewer (not popup)
  if (viewingResource) {
    // For Microsoft Forms, convert the URL to embedded format
    let embedUrl = viewingResource.url;
    if (embedUrl.includes('forms.office.com') || embedUrl.includes('forms.microsoft.com')) {
      // Ensure the URL uses the embed format
      if (!embedUrl.includes('embed=true')) {
        embedUrl = embedUrl.includes('?') ? `${embedUrl}&embed=true` : `${embedUrl}?embed=true`;
      }
    }

    return (
      <div className="fixed inset-0 z-50 bg-white dark:bg-[#0d0d0d] flex flex-col" data-testid="resource-viewer">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1a1a1a] flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setViewingResource(null)}
            data-testid="back-from-resource"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
              viewingResource.file_type === 'video' ? 'bg-red-500/10 text-red-500' :
              viewingResource.file_type === 'document' ? 'bg-blue-500/10 text-blue-500' :
              viewingResource.file_type === 'image' ? 'bg-green-500/10 text-green-500' :
              'bg-[#586D52]/10 text-[#586D52]'
            }`}>
              {getFileTypeIcon(viewingResource.file_type)}
            </div>
            <div className="min-w-0">
              <h1 className="font-semibold text-sm truncate">{viewingResource.title}</h1>
              {viewingResource.description && (
                <p className="text-xs text-muted-foreground truncate">{viewingResource.description}</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Full-page iframe - takes remaining space */}
        <div className="flex-1 overflow-hidden">
          <iframe
            src={embedUrl}
            className="w-full h-full border-0"
            title={viewingResource.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  // Resource Item - Individual container box like recent calls
  const ResourceItem = ({ resource, canDelete, showState }) => (
    <div
      className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 cursor-pointer hover:shadow-md transition-all"
      onClick={() => handleResourceClick(resource)}
      data-testid={`resource-${resource.id}`}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
        resource.file_type === 'video' ? 'bg-red-500/20 text-red-500' :
        resource.file_type === 'document' ? 'bg-blue-500/20 text-blue-500' :
        resource.file_type === 'image' ? 'bg-green-500/20 text-green-500' :
        'bg-[#586D52]/20 text-[#586D52]'
      }`}>
        {getFileTypeIcon(resource.file_type)}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{resource.title}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">
            {resource.file_type}
          </Badge>
          {showState && resource.state && (
            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
              <MapPin className="w-3 h-3" />
              {resource.state}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        {canDelete && resource.created_by === user?.id && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteResource(resource.id);
            }}
            data-testid={`delete-resource-${resource.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
        <ExternalLink className="w-4 h-4 text-muted-foreground" />
      </div>
    </div>
  );

  const ResourceList = ({ resources, canDelete = false, emptyMessage, showState = false }) => (
    <>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : resources.length === 0 ? (
        <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 p-6 text-center text-muted-foreground">
          <File className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{emptyMessage}</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {resources.map((resource) => (
            <ResourceItem 
              key={resource.id}
              resource={resource}
              canDelete={canDelete}
              showState={showState}
            />
          ))}
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="resources-page">
      <div className="max-w-2xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid w-full mb-4 ${isFeatureEnabled('resources_my') && isFeatureEnabled('resources_company') ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {isFeatureEnabled('resources_my') && (
              <TabsTrigger value="my" data-testid="my-resources-tab">
                <UserIcon className="w-4 h-4 mr-2" />
                My Resources
              </TabsTrigger>
            )}
            {isFeatureEnabled('resources_company') && (
              <TabsTrigger value="company" data-testid="company-resources-tab">
                <Building className="w-4 h-4 mr-2" />
                Company Resources
              </TabsTrigger>
            )}
          </TabsList>

          {isFeatureEnabled('resources_my') && (
          <TabsContent value="my" className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Personal Resources</h2>
            <ResourceList 
              resources={myResources} 
              canDelete={true}
              emptyMessage="No personal resources yet"
            />
          </TabsContent>
          )}

          {isFeatureEnabled('resources_company') && (
          <TabsContent value="company" className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Company Resources</h2>
              {user?.role === 'admin' && (
                <Badge variant="outline" className="text-xs">All states</Badge>
              )}
            </div>
            <ResourceList 
              resources={companyResources} 
              canDelete={canAddCompanyResource}
              emptyMessage="No company resources available"
              showState={user?.role === 'admin'}
            />
          </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Add Resource Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-testid="add-resource-dialog">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-lg font-bold">Add Resource</DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">Add a link, document, or video</p>
          </DialogHeader>
          <div className="px-4 pb-4 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="resource-title" className="text-xs font-medium">Title *</Label>
              <Input
                id="resource-title"
                placeholder="Resource title"
                value={newResource.title}
                onChange={(e) => setNewResource(prev => ({ ...prev, title: e.target.value }))}
                className="h-9 rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="resource-url" className="text-xs font-medium">URL *</Label>
              <Input
                id="resource-url"
                placeholder="https://..."
                value={newResource.url}
                onChange={(e) => setNewResource(prev => ({ ...prev, url: e.target.value }))}
                className="h-9 rounded-xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="resource-type" className="text-xs font-medium">Type</Label>
              <Select 
                value={newResource.file_type} 
                onValueChange={(v) => setNewResource(prev => ({ ...prev, file_type: v }))}
              >
                <SelectTrigger id="resource-type" className="h-9 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="link">Link</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="resource-description" className="text-xs font-medium">Description</Label>
              <Textarea
                id="resource-description"
                placeholder="Optional description"
                value={newResource.description}
                onChange={(e) => setNewResource(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="rounded-xl"
              />
            </div>
            {canAddCompanyResource && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is-company-resource"
                  checked={newResource.is_company_resource}
                  onChange={(e) => setNewResource(prev => ({ ...prev, is_company_resource: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="is-company-resource" className="text-sm">
                  Add as company resource
                </Label>
              </div>
            )}
            <Button 
              className="w-full h-10 rounded-xl bg-[#586D52] hover:bg-[#4a5d46] mt-2"
              onClick={handleCreateResource}
            >
              Add Resource
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourcesPage;
